import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { AppError } from "@plura/shared/utils";
import { evaluateLogicItem } from "./evaluateLogicItem";

interface GetPreviousJourneyItemParams {
  leadForm: LeadForm;
  currentJourneyItemId: string;
  userResponses: Record<string, string>;
  isFinalJourneyItem?: boolean;
}

interface PreviousJourneyItemResult {
  previousJourneyItem: FormJourneyItem | null;
}

/**
 * Would forward navigation from `item` jump (via a matched logic rule or a
 * static target) directly to `target`? Mirrors getNextJourneyItem's
 * resolution order so back-navigation stays the inverse of forward.
 */
const jumpsTo = (
  item: FormJourneyItem,
  target: FormJourneyItem,
  journeyItems: FormJourneyItem[],
  userResponses: Record<string, string>,
): boolean => {
  const matchedLogicItem = item.logicItems?.find(logicItem => evaluateLogicItem(logicItem, userResponses));

  if (matchedLogicItem) {
    if (matchedLogicItem.endOnCompletion) return false;
    if (matchedLogicItem.targetBlockId) {
      if (matchedLogicItem.targetBlockId === target.blockId) return true;
      // A rule routing to another existing block means the user was sent
      // elsewhere; a dangling target falls through to the static path,
      // matching getNextJourneyItem.
      if (journeyItems.some(j => j.blockId === matchedLogicItem.targetBlockId)) return false;
    }
  }

  if (item.endOnCompletion) return false;

  return !!item.targetBlockId && item.targetBlockId === target.blockId;
};

export const getPreviousJourneyItem = ({
  leadForm,
  currentJourneyItemId,
  userResponses,
}: GetPreviousJourneyItemParams): PreviousJourneyItemResult => {
  if (!leadForm?.journeyItems?.length) {
    throw new AppError({
      message: 'Journey items not found when getting previous journey item',
      code: 'NOT_FOUND',
    });
  }

  const journeyItems = [...leadForm.journeyItems].sort((a, b) => a.order - b.order);
  const currentIndex = journeyItems.findIndex(item => item.id === currentJourneyItemId);

  if (currentIndex === -1) {
    throw new AppError({
      message: 'Current journey item not found when getting previous journey item',
      code: 'NOT_FOUND',
    });
  }

  const currentJourneyItem = journeyItems[currentIndex];

  // A recorded answer is evidence the user actually visited a step (branch
  // paths that were skipped leave no answers). CONTACT_INFO/ADDRESS register
  // under `${blockId}_${subField}` keys rather than the blockId itself.
  const wasAnswered = (item: FormJourneyItem): boolean =>
    Boolean(userResponses[item.blockId]) ||
    Object.entries(userResponses).some(([key, value]) => key.startsWith(`${item.blockId}_`) && Boolean(value));

  // The nearest earlier item whose branch logic lands on the current one:
  // when such a jump applies, forward navigation skipped everything between
  // it and here, so the plain predecessor was never shown to the user.
  const jumpOrigin = (() => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (jumpsTo(journeyItems[i], currentJourneyItem, journeyItems, userResponses)) {
        return journeyItems[i];
      }
    }
    return null;
  })();
  const sequentialOrigin = journeyItems[currentIndex - 1] ?? null;

  // Prefer origins the user demonstrably visited; among those, a jump
  // beats plain adjacency since an applying jump skips the predecessor.
  const previousJourneyItem =
    (jumpOrigin && wasAnswered(jumpOrigin) ? jumpOrigin : null)
    ?? (sequentialOrigin && wasAnswered(sequentialOrigin) ? sequentialOrigin : null)
    ?? jumpOrigin
    ?? sequentialOrigin;

  return {
    previousJourneyItem: previousJourneyItem ? FormJourneyItem.parse(previousJourneyItem) : null,
  };
};
